/**
 * Norwegian Bokmål grocery aisle taxonomy + keyword seed lookup (Phase 4 D-03 + D-05).
 *
 * Single source of truth for shopping-list auto-categorisation. Both API
 * (shopping.service.add/update) and web (live preview if UI-SPEC asks)
 * import this — no drift possible. Mirrors the allergens.nb.ts pattern.
 *
 * The 8-section enum is FIXED in v1 — adding a category is a v2 code change.
 * `Ukategorisert` is a synthetic UI bucket for `category IS NULL`, not a
 * persisted enum value.
 *
 * Match semantics: case-insensitive substring scan, first-match-wins in
 * declared order. Norwegian Æ/Ø/Å handled by V8's Unicode-aware
 * String.prototype.toLowerCase() — no locale-specific lowercasing needed.
 *
 * Falsey-cases by design (per D-05):
 *   - 'nøtt' lives under torrvarer (nuts visible alongside the allergy chip,
 *     NOT buried in produce — CRIT-1-relevant signal preservation)
 *   - 'egg' is INTENTIONALLY NOT seeded (ambiguous in Norwegian shops);
 *     server returns null and the user picks
 */

export const CATEGORY_KEYS = [
  'frukt_og_gronnsaker',
  'meieri',
  'brod_og_bakeri',
  'kjott_og_fisk',
  'frys',
  'torrvarer',
  'drikke',
  'husholdning',
] as const;
export type CategoryKey = (typeof CATEGORY_KEYS)[number];

export const CATEGORY_LABELS_NB: Record<CategoryKey, string> = {
  frukt_og_gronnsaker: 'Frukt og grønt',
  meieri:              'Meieri',
  brod_og_bakeri:      'Brød og bakeri',
  kjott_og_fisk:       'Kjøtt og fisk',
  frys:                'Frys',
  torrvarer:           'Tørrvarer',
  drikke:              'Drikke',
  husholdning:         'Husholdning',
};

/** Synthetic UI bucket for `category IS NULL` — not a persisted enum value. */
export const UKATEGORISERT_LABEL_NB = 'Ukategorisert';

/** Display order = declaration order. 1 = top of list. */
export const CATEGORY_ORDER: Record<CategoryKey, number> =
  Object.fromEntries(CATEGORY_KEYS.map((k, i) => [k, i + 1])) as Record<CategoryKey, number>;

/**
 * Per-category keyword roots (full D-05 list).
 *
 * Ordering within an array does not matter (first-match across categories
 * is what wins via CATEGORY_KEYS iteration). However, prefer LONGER variants
 * over SHORTER substrings within the same category to avoid Pitfall 8
 * compound-word over-match (`iskrem` before `is`, `frosset` before `frossen`).
 */
export const CATEGORY_KEYWORDS: Record<CategoryKey, readonly string[]> = {
  frukt_og_gronnsaker: [
    'eple', 'pære', 'banan', 'appelsin', 'sitron', 'lime', 'drue', 'jordbær',
    'bringebær', 'blåbær', 'bær', 'tomat', 'agurk', 'salat', 'paprika',
    'gulrot', 'hvitløk', 'løk', 'potet', 'brokkoli', 'blomkål', 'kål',
    'purre', 'selleri', 'avokado', 'mango', 'melon', 'frukt', 'grønnsak',
    'grønt',
  ],
  meieri: [
    'havremelk', 'mandelmelk', 'soyamelk', 'lettmelk', 'helmelk', 'melk',
    'yoghurt', 'kvarg', 'cottage', 'fløte', 'crème fraiche', 'creme fraiche',
    'crème', 'rømme', 'smør', 'margarin', 'gulost', 'brunost', 'kremost',
    'feta', 'parmesan', 'ost',
  ],
  brod_og_bakeri: [
    'knekkebrød', 'rundstykke', 'baguette', 'brød', 'loff', 'lefse',
    'vaffel', 'bolle', 'kringle', 'kake', 'bakeri',
  ],
  kjott_og_fisk: [
    'kjøttdeig', 'kjøttbolle', 'kjøtt', 'kylling', 'kalkun', 'svin', 'lam',
    'biff', 'okse', 'pølse', 'bacon', 'skinke', 'fiskekake', 'laks',
    'torsk', 'tunfisk', 'makrell', 'sild', 'reker', 'skalldyr', 'sushi',
    'fisk',
  ],
  frys: [
    'iskrem', 'is', 'frossen', 'frosset', 'frosne', 'frys', 'pizza',
    'pommes frites', 'nuggets',
  ],
  torrvarer: [
    'spaghetti', 'pasta', 'nudler', 'ris', 'mel', 'sukker', 'salt', 'pepper',
    'krydder', 'bønner', 'linser', 'havregryn', 'cornflakes',
    'frokostblanding', 'müsli', 'nøtt', 'mandel', 'rosin', 'tørket',
    'hermetikk', 'boks', 'tomatpuré', 'kjeks', 'sjokolade', 'godteri',
    'snacks', 'chips', 'popcorn',
  ],
  drikke: [
    'mineralvann', 'vann', 'brus', 'cola', 'farris', 'eplejuice',
    'appelsinjuice', 'juice', 'saft', 'kaffe', 'te', 'øl', 'vin', 'sider',
    'smoothie',
  ],
  husholdning: [
    'såpe', 'oppvask', 'vaskemiddel', 'tøymykner', 'klorin', 'toalettpapir',
    'tørkerull', 'kjøkkenrull', 'serviett', 'aluminiumsfolie', 'bakepapir',
    'plastfolie', 'søppelpose', 'pose', 'oppvaskbørste', 'svamp',
    'tannkrem', 'tannbørste', 'shampo', 'balsam', 'bleier', 'bind',
    'tampong', 'batteri', 'lyspære',
  ],
};

/**
 * Server-side categoriser. Returns the first matching CategoryKey or `null`.
 *
 * Iterates `CATEGORY_KEYS` in declaration order so disambiguation is
 * deterministic — `brød` matches `brod_og_bakeri` BEFORE the iteration
 * would consider `tørrvarer`.
 *
 * Performance: O(K * W * |text|) where K=8 categories, W≈20 keywords each,
 * |text|≈20 chars. ~3200 substring checks worst-case = sub-millisecond on V8.
 * No memoisation — family scale never warrants it.
 */
export function categorise(text: string): CategoryKey | null {
  const haystack = text.toLowerCase();
  for (const key of CATEGORY_KEYS) {
    for (const kw of CATEGORY_KEYWORDS[key]) {
      if (haystack.includes(kw)) return key;
    }
  }
  return null;
}
